'use client';

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserLogged } from '@/hooks/useUserLogged';
import { fetchOrgAuthInfo } from '@/apis/ApiUtil';
import type AuthPayload from '@/types/authPayloadType';

/** Response shape of POST /fetchAuthInfo/ for an org-wide lookup. */
export interface OrgAuthInfoResponse {
  Integrations?: AuthPayload[];
  Error?: string;
}

/**
 * Shared org integration auth-info query (POST /fetchAuthInfo/).
 *
 * Every consumer goes through this hook so the whole app shares one cache entry
 * under ['orgAuthInfo', orgUUID]. The same endpoint used to be fetched under
 * four different query key shapes, so one page load issued several identical
 * requests that could not dedupe.
 *
 * The org UUID always comes from useUserLogged() rather than a store snapshot,
 * so the key cannot diverge between call sites.
 *
 * UserUUID is deliberately NOT sent. The backend (organization/repository.py,
 * fetch_integration_repo) filters the org branch on orgUUID, but when that
 * returns nothing and a userUUID was supplied it falls back to
 * filter(userUUID=...). Since orgUUID is nullable on that model, the fallback
 * can return the caller's personal integration rows from other orgs as if they
 * belonged to the current org. Sending only OrgUUID keeps this query purely
 * org-scoped: an org with no integrations reads as "nothing connected", which
 * is the correct answer.
 *
 * @param enabled Extra gate ANDed with "org is known". Use it for consumers that
 *   should not trigger the request themselves (for example a closed modal); they
 *   still read the cached value once another consumer has fetched it.
 */
export function useOrgAuthInfo({ enabled = true }: { enabled?: boolean } = {}) {
  const { user, organization } = useUserLogged();
  const orgUUID = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  const query = useQuery<OrgAuthInfoResponse>({
    queryKey: ['orgAuthInfo', orgUUID],
    queryFn: () => fetchOrgAuthInfo(orgUUID),
    enabled: !!orgUUID && enabled,
    staleTime: 60 * 1000,
  });

  // Empty on error or missing payload so callers can iterate without repeating
  // the Error guard. Referentially stable while the query data is unchanged,
  // which keeps it safe to use as a useMemo dependency.
  const integrations = useMemo<AuthPayload[]>(() => {
    const data = query.data;
    if (!data || data.Error || !Array.isArray(data.Integrations)) return [];
    return data.Integrations;
  }, [query.data]);

  /** Whether the org has the given backend integration name (case-insensitive). */
  // Memoized so consumers can safely put it in a dep array or pass it as a prop.
  const hasIntegration = useCallback(
    (apiName: string) =>
      integrations.some((i) => i.IntegrationName?.toLowerCase() === apiName),
    [integrations]
  );

  return {
    orgUUID,
    data: query.data,
    integrations,
    hasIntegration,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
  };
}
